/* Aloha with Us — product page: size guide dialog, unit toggle, buy box (style pills + matching set), sticky add to cart. */
(() => {
  const formatMoney = (cents, format) => {
    const value = Number(cents) / 100;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, decimals, thousands, decimal) => {
      const [int, frac] = n.toFixed(decimals).split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    const match = (format || '${{amount}}').match(placeholder);
    if (!match) return value.toFixed(2);
    const out = {
      amount: fmt(value, 2, ',', '.'),
      amount_no_decimals: fmt(value, 0, ',', '.'),
      amount_with_comma_separator: fmt(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(value, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(value, 2, "'", '.'),
      amount_with_space_separator: fmt(value, 2, ' ', ','),
    }[match[1]];
    return format.replace(placeholder, out ?? value.toFixed(2));
  };

  /* Dialogs: open with [data-dialog-open], return focus to the opener on close. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (!opener) return;
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (!dialog || typeof dialog.showModal !== 'function') return;
    dialog.showModal();
    dialog.addEventListener('close', () => opener.focus(), { once: true });
  });
  document.addEventListener('click', (event) => {
    if (event.target.matches('dialog.aw-dialog[open]')) event.target.close();
  });

  /* Size guide: product type tabs and the inches / cm toggle. */
  document.addEventListener('click', (event) => {
    const guide = event.target.closest('[data-size-guide]');
    if (!guide) return;

    const unitButton = event.target.closest('[data-unit-button]');
    if (unitButton) {
      guide.dataset.unit = unitButton.dataset.unitButton;
      guide.querySelectorAll('[data-unit-button]').forEach((b) => b.setAttribute('aria-pressed', String(b === unitButton)));
      return;
    }

    const tab = event.target.closest('[data-tab]');
    if (!tab) return;
    guide.querySelectorAll('[data-tab]').forEach((t) => {
      const active = t === tab;
      t.setAttribute('aria-pressed', String(active));
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !active;
    });
    const title = guide.dataset.titleId && document.getElementById(guide.dataset.titleId);
    if (title) title.textContent = tab.dataset.title;
  });

  /*
   * Buy box (snippets/aw-buy-box.liquid): one checkbox row per piece of the design. A ticked piece opens its size
   * pills and size finder; picking a size closes the row to a "Size XL" chip (tap to change). One button adds every
   * ticked piece in one /cart/add.js call and opens the cart drawer. Single-piece products show their sizes only.
   * Drives Dawn's product form (hidden variant id, button label), the price, the URL (?variant=) and the gallery,
   * and announces its state with an `aw:buybox` event for the sticky bar.
   */
  class AwBuyBox extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-buy-box-json]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.pieces = this.data.pieces;
      this.variants = this.data.variants;
      this.moneyFormat = this.dataset.moneyFormat;
      this.sectionId = this.dataset.section;
      this.info = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId);
      this.submit = this.form?.querySelector('[type="submit"]');
      this.idInput = this.form?.querySelector('input[name="id"]');
      this.multi = this.pieces.length > 1;
      this.notes = new Map(); // piece → measurements from the size finder (cart line property "Size finder")
      this.chosen = new Map(); // ticked piece → chosen variant, or null while its size is missing (tick order)
      this.open = new Set(); // ticked pieces showing their sizes

      const selected = this.variants.find((v) => v.id === this.data.selected);
      if (selected) this.chosen.set(selected.piece, selected);
      if (!this.multi) {
        if (!selected) this.chosen.set(0, null);
        this.open.add(0);
      }
      this.last = selected ?? null;

      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-piece-toggle]');
        if (toggle && !toggle.disabled) return this.togglePiece(this.pieceOf(toggle));
        const change = event.target.closest('[data-piece-change]');
        if (change) return this.openPiece(this.pieceOf(change));
        const tool = event.target.closest('[data-piece-tool]');
        if (tool) return this.toggleTool(this.pieceOf(tool), tool.dataset.pieceTool);
        const size = event.target.closest('[data-variant]');
        if (size && !size.disabled) this.selectVariant(Number(size.dataset.variant));
      });
      if (this.form) {
        // Capture on the ancestor runs before Dawn's submit listener: our add sends every ticked piece at once, and
        // without a complete choice the button points the customer to what is missing (it is never disabled).
        this.onSubmit = (event) => {
          if (event.target !== this.form) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.state().ready) this.addChosen();
          else this.focusMissing();
        };
        this.form.parentElement.addEventListener('submit', this.onSubmit, true);
      }
      this.update(false);
    }

    disconnectedCallback() {
      this.form?.parentElement?.removeEventListener('submit', this.onSubmit, true);
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    pieceOf(element) {
      return Number(element.closest('li[data-piece]').dataset.piece);
    }

    pieceVariants(index) {
      return this.variants.filter((v) => v.piece === index);
    }

    priceText(list) {
      const prices = list.map((v) => v.price);
      const min = Math.min(...prices);
      return `${Math.max(...prices) > min ? 'From ' : ''}${this.money(min)}`;
    }

    noun(index) {
      const noun = this.pieces[index]?.noun;
      return noun && noun !== 'item' ? noun : '';
    }

    togglePiece(index) {
      if (this.chosen.has(index)) {
        this.chosen.delete(index);
        this.open.delete(index);
        this.notes.delete(index);
      } else {
        this.chosen.set(index, null);
        this.open.add(index);
        this.showMedia(this.pieces[index]?.media);
        this.syncSizeGuide(index);
      }
      this.update(true);
    }

    openPiece(index) {
      this.open.add(index);
      this.update(true);
      this.querySelector(`[data-piece="${index}"] [data-variant][aria-pressed="true"], [data-piece="${index}"] [data-variant]:not([disabled])`)?.focus();
    }

    /** A piece's "Size chart" / "Find my size" panels: one open at a time; the same button closes it. */
    toggleTool(index, name) {
      const row = this.querySelector(`li[data-piece="${index}"]`);
      row?.querySelectorAll('[data-piece-tool]').forEach((button) => {
        const panel = row.querySelector(`[data-piece-panel="${button.dataset.pieceTool}"]`);
        if (!panel) return;
        const open = button.dataset.pieceTool === name ? panel.hidden : false;
        panel.hidden = !open;
        button.setAttribute('aria-expanded', String(open));
        if (open && name === 'finder') panel.querySelector('input')?.focus();
      });
    }

    /** keepOpen: the size finder chose the size, so its explanation stays visible. */
    selectVariant(id, keepOpen = false) {
      const variant = this.variants.find((v) => v.id === id);
      if (!variant) return;
      this.chosen.set(variant.piece, variant);
      if (this.multi && !keepOpen) this.open.delete(variant.piece);
      this.last = variant;
      this.showMedia(variant.media ?? this.pieces[variant.piece]?.media);
      this.update(true);
    }

    /** Button / sticky state for the ticked pieces. */
    state() {
      const add = window.variantStrings?.addToCart || 'Add to cart';
      const list = [...this.chosen.entries()];
      if (!list.length) return { ready: false, label: add, hint: 'Choose at least one piece' };
      const missing = list.find(([, v]) => !v);
      if (missing) {
        const noun = this.noun(missing[0]);
        const text = noun ? `Choose a ${noun} size` : 'Choose a size';
        return { ready: false, label: text, hint: text, missing: missing[0] };
      }
      if (list.some(([, v]) => !v.available)) return { ready: false, soldOut: true, label: window.variantStrings?.soldOut || 'Sold out' };
      const total = list.reduce((sum, [, v]) => sum + v.price, 0);
      if (list.length === 1) {
        const noun = this.noun(list[0][0]);
        return { ready: true, label: noun ? `Add ${noun} to cart` : add, total };
      }
      return { ready: true, label: `Add ${list.length} pieces to cart · ${this.money(total)}`, total };
    }

    update(fromUser) {
      this.querySelectorAll('li[data-piece]').forEach((row) => {
        const index = Number(row.dataset.piece);
        const ticked = this.chosen.has(index);
        const variant = this.chosen.get(index);
        row.classList.toggle('is-checked', ticked);
        row.querySelector('[data-piece-toggle]')?.setAttribute('aria-checked', String(ticked));
        const body = row.querySelector('[data-piece-body]');
        if (body) body.hidden = !this.open.has(index);
        const chip = row.querySelector('[data-piece-change]');
        if (chip) {
          chip.hidden = !variant || this.open.has(index);
          if (variant) {
            chip.textContent = `Size ${variant.size}`;
            chip.setAttribute('aria-label', `Size ${variant.size}, change size`);
          }
        }
        row.querySelectorAll('[data-variant]').forEach((pill) => pill.setAttribute('aria-pressed', String(Number(pill.dataset.variant) === variant?.id)));
      });

      const state = this.state();
      const price = document.getElementById(`price-${this.sectionId}`);
      if (price && fromUser) {
        const ticked = [...this.chosen.keys()];
        const text = state.ready ? this.money(state.total) : this.priceText(ticked.length === 1 ? this.pieceVariants(ticked[0]) : this.variants);
        price.innerHTML = `<div class="price price--large"><div class="price__container"><div class="price__regular"><span class="visually-hidden visually-hidden--inline">${window.variantStrings?.regularPrice || 'Price'}</span><span class="price-item price-item--regular"></span></div></div></div>`;
        price.querySelector('.price-item').textContent = text;
      }
      const first = [...this.chosen.values()].find(Boolean);
      if (this.idInput) {
        this.idInput.value = first ? String(first.id) : '';
        this.idInput.disabled = false;
        if (fromUser) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.submit) {
        this.submit.disabled = !!state.soldOut;
        const text = this.submit.querySelector('span');
        if (text) text.textContent = state.label;
        this.submit.dataset.addLabel = state.ready ? state.label : '';
      }
      const hint = this.querySelector('[data-buy-hint]');
      if (hint && !hint.hidden && fromUser) {
        if (state.ready) hint.hidden = true;
        else hint.textContent = state.hint;
      }
      this.querySelectorAll('.is-attention').forEach((el) => el.classList.remove('is-attention'));

      if (fromUser) {
        const url = new URL(window.location.href);
        const shown = this.last && this.chosen.get(this.last.piece)?.id === this.last.id ? this.last : first;
        if (shown) url.searchParams.set('variant', shown.id);
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
      this.announce(state);
    }

    /** Shows one gallery item without scrolling the page (Dawn's setActiveMedia scrolls up to the gallery). */
    showMedia(media) {
      if (!media) return;
      const mediaId = `${this.sectionId}-${media}`;
      const gallery = this.info?.querySelector('media-gallery');
      const viewer = gallery?.elements?.viewer;
      const item = viewer?.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      window.pauseAllMedia?.();
      viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.toggle('is-active', el === item));
      const list = item.parentElement;
      if (list.scrollWidth > list.clientWidth) list.scrollTo({ left: item.offsetLeft - list.offsetLeft, behavior: 'smooth' });
      const thumbnail = gallery.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
      if (thumbnail) gallery.setActiveThumbnail(thumbnail);
    }

    /** Opens the size guide on the piece's tab. */
    syncSizeGuide(index) {
      const code = this.pieces[index]?.code;
      if (!code) return;
      const tab = this.info?.querySelector(`[data-size-guide] [data-code="${code}"]`);
      if (tab && tab.getAttribute('aria-pressed') !== 'true') tab.click();
    }

    announce(state = this.state()) {
      const list = [...this.chosen.entries()];
      const short = (index) => (this.multi ? `${this.noun(index) ? this.noun(index)[0].toUpperCase() + this.noun(index).slice(1) : this.pieces[index].name} ` : '');
      let meta;
      if (!list.length) meta = this.priceText(this.variants);
      else if (state.ready) meta = `${list.map(([index, v]) => `${short(index)}${v.size}`).join(' + ')} · ${this.money(state.total)}`;
      else meta = list.map(([index, v]) => `${short(index)}${v ? v.size : '–'}`).join(' + ');
      this.dispatchEvent(new CustomEvent('aw:buybox', { bubbles: true, detail: { ...state, meta } }));
    }

    /** Scrolls to the first missing choice and focuses it (add button / sticky bar before the choice is complete). */
    focusMissing() {
      const state = this.state();
      let group;
      let target;
      if (state.missing != null) {
        this.open.add(state.missing);
        this.update(false);
        group = this.querySelector(`[data-piece="${state.missing}"]`);
        target = group.querySelector('[data-variant]:not([disabled])');
      } else {
        group = this.querySelector('[data-pieces-group]');
        target = this.querySelector('[data-piece-toggle]:not([disabled])');
      }
      const hint = this.querySelector('[data-buy-hint]');
      if (hint && state.hint) {
        hint.textContent = state.hint;
        hint.hidden = false;
      }
      if (group) {
        group.classList.remove('is-attention');
        void group.offsetWidth; // restart the highlight animation
        group.classList.add('is-attention');
      }
      (hint && !hint.hidden ? hint : target ?? this).scrollIntoView({ block: 'center', behavior: 'smooth' });
      target?.focus({ preventScroll: true });
    }

    showError(message) {
      let error = this.error;
      if (!error) {
        error = this.error = Object.assign(document.createElement('p'), { className: 'aw-buy__error' });
        error.setAttribute('role', 'alert');
      }
      this.submit?.closest('.product-form')?.after(error);
      error.textContent = message;
      error.hidden = !message;
    }

    /** One /cart/add.js call with every ticked piece, then the cart drawer (or the cart page). */
    async addChosen() {
      const button = this.submit;
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      const items = [...this.chosen.entries()].map(([piece, v]) => {
        const item = { id: v.id, quantity: 1 };
        if (this.notes.has(piece)) item.properties = { 'Size finder': this.notes.get(piece) };
        return item;
      });
      const cart = document.querySelector('cart-drawer');
      const body = { items };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      if (this.error) this.error.hidden = true;
      try {
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.showError(data.description || data.message || window.cartStrings?.error || 'Could not add to cart.');
          return;
        }
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'aw-buy-box', cartData: data });
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }
        cart.classList.remove('is-empty');
        cart.setActiveElement(button);
        cart.renderContents(data);
      } catch (e) {
        this.showError(window.cartStrings?.error || 'Could not add to cart. Please try again.');
      } finally {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }
  }
  if (!customElements.get('aw-buy-box')) customElements.define('aw-buy-box', AwBuyBox);

  /*
   * Size finder (snippets/aw-size-finder.liquid), one per piece of the buy box, opened by its "Find my size" button.
   * Uses only the official size chart (buy box JSON): the smallest size whose chart values are at least the
   * customer's measurements; within the chart tolerance of the size below it says "between sizes" and keeps the
   * size up (the chart's tip). Selects that size and keeps the measurements for the piece's cart line
   * ("Size finder" property, see AwBuyBox.addChosen).
   */
  class AwSizeFinder extends HTMLElement {
    // Which optional measurements each type asks for. garment: the chart is a shirt laid flat (kids' shirt: chest
    // 13.6–18 in), so the customer measures a well-fitting shirt laid flat the same way. Types without a rule
    // show the size chart only.
    static RULES = {
      MHS: { shoulder: true },
      KHS: { shoulder: true, garment: true },
      WMD: { hip: true },
      WLD: { hip: true },
      GDR: { kids: true },
      // New dresses (owner 2026-10-28: suggest from the ToAddit charts as they are): each asks for the optional
      // measurements its chart has (WTM has no Hip or Shoulder column, so bust only).
      WMM: { hip: true },
      WTM: {},
      WSM: { shoulder: true },
      WPL: { hip: true },
      WSB: { hip: true },
    };
    static CM = 2.54;

    connectedCallback() {
      this.buyBox = this.closest('aw-buy-box');
      if (!this.buyBox?.data) return;
      this.piece = Number(this.dataset.finderPiece);
      this.code = (this.buyBox.pieces[this.piece]?.code || this.buyBox.data.defaultCode || '').toUpperCase();
      this.chart = this.buyBox.data.charts?.[this.code];
      this.rule = AwSizeFinder.RULES[this.code];
      if (!this.chart || !this.rule) return;
      this.hidden = false;
      const button = this.closest('li[data-piece]')?.querySelector('[data-piece-tool="finder"]');
      if (button) button.hidden = false;
      this.unit = 'in';
      const garment = !!this.rule.garment;
      this.querySelector('[data-finder-bust-label]').textContent = garment ? 'Shirt Chest, Laid Flat' : this.rule.kids ? "Child's Chest Measurement" : 'Bust Measurement';
      this.querySelector('[data-finder-shoulder-label]').textContent = garment ? 'Shirt Shoulder, Laid Flat' : 'Shoulder Measurement';
      this.querySelectorAll('[data-finder-row], [data-finder-how]').forEach((el) => {
        const row = el.dataset.finderRow;
        const how = el.dataset.finderHow;
        el.hidden = (row && !this.rule[row]) || (how && how !== (garment ? 'garment' : 'body'));
      });
      if (garment) this.querySelector('details.aw-finder__how').open = true;
      this.setPlaceholders();
      this.querySelector('[data-finder-form]').addEventListener('submit', (event) => {
        event.preventDefault();
        this.find();
      });
      this.querySelectorAll('[data-finder-unit]').forEach((b) => b.addEventListener('click', () => this.setUnit(b.dataset.finderUnit)));
      this.querySelector('[data-finder-result]').addEventListener('click', (event) => {
        if (!event.target.closest('[data-finder-chart]')) return;
        event.preventDefault();
        this.buyBox.toggleTool(this.piece, 'chart');
      });
    }

    setUnit(unit) {
      if (unit === this.unit) return;
      const { CM } = AwSizeFinder;
      this.querySelectorAll('[data-finder-input]').forEach((input) => {
        const v = parseFloat(input.value);
        if (!isNaN(v)) input.value = (unit === 'cm' ? v * CM : v / CM).toFixed(1).replace(/\.0$/, '');
      });
      this.unit = unit;
      this.querySelectorAll('[data-finder-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.finderUnit === unit)));
      this.querySelectorAll('[data-finder-unit-label]').forEach((s) => (s.textContent = unit === 'cm' ? 'cm' : 'inches'));
      this.setPlaceholders();
      this.showResult('');
    }

    /** Example values in the inputs: the middle size of this piece's chart, in the current unit. */
    setPlaceholders() {
      const row = this.chart.rows[Math.floor(this.chart.rows.length / 2)];
      const names = { bust: 'Bust', hip: 'Hip', shoulder: 'Shoulder' };
      this.querySelectorAll('[data-finder-input]').forEach((input) => {
        const i = this.chart.cols.indexOf(names[input.dataset.finderInput]);
        const value = i > -1 ? row[this.unit === 'cm' ? 'cm' : 'inches']?.[i] : null;
        input.placeholder = value ? `e.g. ${Math.round(value)}` : '';
      });
    }

    /** The chart's "actual sizes may vary by up to ±N inch" (dresses ±2, shirts ±1). */
    tolerance() {
      return Number(this.chart.tolerance) || 1;
    }

    /** Smallest size meeting every given measurement: { index, between (index below, or -1), by } or { tooBig }. */
    recommend(body) {
      const { chart, rule } = this;
      const col = (name) => chart.cols.indexOf(name);
      const checks = [];
      const bust = col('Bust');
      if (bust > -1) checks.push({ by: 'bust', need: body.bust, value: (r) => r.inches[bust] });
      if (rule.hip && body.hip && col('Hip') > -1) checks.push({ by: 'hips', need: body.hip, value: (r) => r.inches[col('Hip')] });
      if (rule.shoulder && body.shoulder && col('Shoulder') > -1) checks.push({ by: 'shoulders', need: body.shoulder, value: (r) => r.inches[col('Shoulder')] });
      if (!checks.length) return null;
      let index = -1;
      let by = 'bust';
      for (const check of checks) {
        const i = chart.rows.findIndex((r) => check.value(r) >= check.need);
        if (i === -1) return { tooBig: true, largest: chart.rows[chart.rows.length - 1].size };
        if (i > index) {
          index = i;
          by = check.by;
        }
      }
      const below = chart.rows[index - 1];
      const between = below && checks.every((c) => c.value(below) >= c.need - this.tolerance()) ? index - 1 : -1;
      return { index, between, by };
    }

    find() {
      const { chart, rule } = this;
      const toInches = (v) => (this.unit === 'cm' ? v / AwSizeFinder.CM : v);
      const read = (name) => {
        const input = this.querySelector(`[data-finder-input="${name}"]`);
        const v = parseFloat(input.value);
        return input.closest('[hidden]') || !(v > 0) ? 0 : v;
      };
      const raw = { bust: read('bust'), hip: rule.hip ? read('hip') : 0, shoulder: rule.shoulder ? read('shoulder') : 0 };
      this.querySelector('[data-finder-error]').hidden = true;
      if (!raw.bust) return this.showError(rule.garment ? "Please enter the shirt's chest, laid flat." : rule.kids ? "Please enter your child's chest measurement." : 'Please enter your bust measurement.');
      const body = { bust: toInches(raw.bust), hip: toInches(raw.hip), shoulder: toInches(raw.shoulder) };
      const unusual = (v, min, max) => v && (v < min || v > max);
      // Sanity check for typing mistakes (cm entered as inches, missing digit); it never changes the suggestion.
      if (unusual(body.bust, rule.garment ? 8 : rule.kids ? 14 : 24, rule.garment ? 40 : 80) || unusual(body.hip, 24, 90) || unusual(body.shoulder, 6, 35)) {
        return this.showError(`That measurement looks unusual — please check it (you are using ${this.unit === 'cm' ? 'cm' : 'inches'}).`);
      }

      const result = this.recommend(body);
      if (!result) return;
      if (result.tooBig) {
        this.buyBox.notes.delete(this.piece);
        return this.showResult(
          `Your measurements are above our largest ${chart.name} size (${result.largest}).<small>Please <a href="/pages/contact">contact us</a> before ordering and we'll help you choose.</small>`
        );
      }
      const size = chart.rows[result.index].size;
      const variant = this.buyBox.pieceVariants(this.piece).find((v) => v.size === size);
      const unit = this.unit === 'cm' ? 'cm' : 'in';
      const parts = [`${rule.garment ? 'shirt laid flat: chest' : 'bust'} ${raw.bust} ${unit}`];
      if (raw.hip) parts.push(`hip ${raw.hip} ${unit}`);
      if (raw.shoulder) parts.push(`shoulder ${raw.shoulder} ${unit}`);
      this.buyBox.notes.set(this.piece, `${parts.join(', ')} (suggested ${size})`);

      const head =
        result.between > -1
          ? `You're between <b>${chart.rows[result.between].size}</b> and <b>${size}</b> — we suggest <strong>${size}</strong> (between sizes, we recommend one size up).`
          : `Recommended size: <strong>${size}</strong>.`;
      let status;
      if (variant?.available) {
        this.buyBox.selectVariant(variant.id, true);
        status = ' Selected for you.';
      } else {
        status = ` Size ${size} is sold out in this style.`;
      }
      const why = result.by === 'hips' ? 'Sized to fit your hips. ' : result.by === 'shoulders' ? 'Sized to fit your shoulders. ' : '';
      this.showResult(
        `${head}${status}<small>${why}Based on our size chart (actual sizes may vary by up to ±${this.tolerance()} inch). ` +
          `Please check the <a href="/pages/size-guide" data-finder-chart>size chart</a> if unsure.</small>`
      );
    }

    showError(message) {
      const error = this.querySelector('[data-finder-error]');
      error.textContent = message;
      error.hidden = false;
      this.showResult('');
    }

    showResult(html) {
      const box = this.querySelector('[data-finder-result]');
      box.innerHTML = html;
      box.hidden = !html;
    }
  }
  if (!customElements.get('aw-size-finder')) customElements.define('aw-size-finder', AwSizeFinder);

  /*
   * Sticky add to cart (mobile): shows once the buy button scrolls above the screen and mirrors the buy box.
   * Before a style / size is chosen its button scrolls to the missing choice instead of being disabled.
   */
  class AwStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.buyBox = document.querySelector('aw-buy-box');
      this.button = this.querySelector('[data-sticky-submit]');
      this.meta = this.querySelector('[data-sticky-meta]');
      const target = this.form?.closest('.product-form') || this.form;
      if (!target || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      // The root reaches far below the screen, so the target only stops intersecting once it is above the
      // viewport. A plain viewport root misses jumps from below the button to above it (anchor, fast fling).
      this.observer = new IntersectionObserver(
        (entries) => this.classList.toggle('is-visible', !entries[entries.length - 1].isIntersecting),
        { threshold: 0, rootMargin: '0px 0px 100000px 0px' }
      );
      this.observer.observe(target);

      this.button.addEventListener('click', () => {
        if (this.state && !this.state.ready && !this.state.soldOut && this.buyBox) {
          this.buyBox.focusMissing();
          return;
        }
        this.form.querySelector('[type="submit"]')?.click();
      });
      this.onBuyBox = (event) => this.render(event.detail);
      document.addEventListener('aw:buybox', this.onBuyBox);
      if (this.buyBox?.data) this.buyBox.announce();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('aw:buybox', this.onBuyBox);
    }

    render(state) {
      this.state = state;
      this.meta.textContent = state.meta;
      this.button.textContent = state.label;
      this.button.disabled = !!state.soldOut;
    }
  }
  if (!customElements.get('aw-sticky-atc')) customElements.define('aw-sticky-atc', AwStickyAtc);

  /* Related products: drop pieces from the same design group (they are in the buy box), then trim. */
  const filterRecommendations = (container) => {
    const items = [...container.querySelectorAll('li[data-design-group]')];
    if (!items.length) return;
    const exclude = container.dataset.excludeGroup;
    const max = Number(container.dataset.max) || items.length;
    let kept = 0;
    items.forEach((item) => {
      const drop = (exclude && item.dataset.designGroup === exclude) || kept >= max;
      if (drop) item.remove();
      else kept += 1;
    });
    if (kept === 0) container.closest('.shopify-section')?.setAttribute('hidden', '');
  };
  document.querySelectorAll('product-recommendations[data-max]').forEach((container) => {
    new MutationObserver(() => filterRecommendations(container)).observe(container, { childList: true });
  });
})();
